export type VehicleSilhouetteId =
  | 'classic_muscle'
  | 'camaro_ss'
  | 'vintage_truck'
  | 'mustang_fastback'
  | 'deuce_roadster'
  | 'custom_upload';

export type PaintFinishType =
  | 'acrylic_lacquer'
  | 'satin_primer'
  | 'metallic_enamel';

export type WheelArchetype =
  | 'slotted_mags'
  | 'steelies'
  | 'torq_thrusts'
  | 'mesh';

export type WheelDiameter = 15 | 17 | 18 | 20;

export type WheelFinish =
  | 'polished'
  | 'machined_gray'
  | 'matte_black'
  | 'chrome'
  | 'gold';

export type TireTreatment =
  | 'blackwall'
  | 'white_letter'
  | 'redline'
  | 'goldline';

export interface StanceSettings {
  frontHeightOffsetInches: number; // -4.0 to +2.0, step 0.25
  rearHeightOffsetInches: number;  // -4.0 to +2.0, step 0.25
  frontWheelDiameter: WheelDiameter;
  rearWheelDiameter: WheelDiameter;
  staggeredWheels: boolean;
}

export interface PaintSettings {
  finishType: PaintFinishType;
  colorHex: string;
  presetName?: string;
  clearcoatGloss: number;          // 0.0 (matte) to 1.0 (mirror)
  metallicFleckDensity: number;    // 0.0 to 1.0
}

export interface WheelSettings {
  archetype: WheelArchetype;
  finish: WheelFinish;
  tireTreatment: TireTreatment;
  hasBeautyRings?: boolean;        // for steelies
  hasRivetBolts?: boolean;         // for mesh
}

export interface VisualizerState {
  version: 1;
  id: string;
  name: string;
  updatedAt: string;
  silhouetteId: VehicleSilhouetteId;
  customSilhouetteDataUrl?: string;
  stance: StanceSettings;
  paint: PaintSettings;
  wheel: WheelSettings;
  showDimensionHud: boolean;
}

export interface SilhouetteData {
  id: VehicleSilhouetteId;
  name: string;
  yearModelDescription: string;
  wheelbaseInches: number;
  wheelbasePx: number;
  frontHubX: number;
  rearHubX: number;
  hubY0: number;
  groundY: number;
  frontArchRadius: number;
  rearArchRadius: number;
  scalePxPerInch: number;
}

export interface PaintPreset {
  id: string;
  name: string;
  colorHex: string;
  finishType: PaintFinishType;
  clearcoatGloss: number;
  metallicFleckDensity: number;
  eraDescription: string;
}

export const SILHOUETTES: Record<Exclude<VehicleSilhouetteId, 'custom_upload'>, SilhouetteData> = {
  classic_muscle: {
    id: 'classic_muscle',
    name: '1969 Fastback Muscle Coupe',
    yearModelDescription: '1969 Classic Fastback Hardtop (e.g. Charger, Mustang, Camaro)',
    wheelbaseInches: 108,
    wheelbasePx: 480,
    frontHubX: 260,
    rearHubX: 740,
    hubY0: 340,
    groundY: 400,
    frontArchRadius: 72,
    rearArchRadius: 74,
    scalePxPerInch: 4.444,
  },
  camaro_ss: {
    id: 'camaro_ss',
    name: '1969 Chevrolet Camaro SS',
    yearModelDescription: '1969 Camaro SS 396 / Z28 Classic Hardtop',
    wheelbaseInches: 108,
    wheelbasePx: 480,
    frontHubX: 260,
    rearHubX: 740,
    hubY0: 340,
    groundY: 400,
    frontArchRadius: 72,
    rearArchRadius: 74,
    scalePxPerInch: 4.444,
  },
  vintage_truck: {
    id: 'vintage_truck',
    name: '1967-72 Fleetside Shop Truck',
    yearModelDescription: '1967-72 C10 Fleetside Short Bed Pickup',
    wheelbaseInches: 115,
    wheelbasePx: 510,
    frontHubX: 245,
    rearHubX: 755,
    hubY0: 340,
    groundY: 400,
    frontArchRadius: 76,
    rearArchRadius: 78,
    scalePxPerInch: 4.435,
  },
  mustang_fastback: {
    id: 'mustang_fastback',
    name: '1969 Mustang Fastback',
    yearModelDescription: '1969 Mustang SportsRoof Fastback Hardtop',
    wheelbaseInches: 108,
    wheelbasePx: 480,
    frontHubX: 255,
    rearHubX: 735,
    hubY0: 340,
    groundY: 400,
    frontArchRadius: 74,
    rearArchRadius: 75,
    scalePxPerInch: 4.444,
  },
  deuce_roadster: {
    id: 'deuce_roadster',
    name: '1932 Deuce Highboy Roadster',
    yearModelDescription: '1932 Highboy Roadster / 3-Window Coupe',
    wheelbaseInches: 106,
    wheelbasePx: 470,
    frontHubX: 265,
    rearHubX: 735,
    hubY0: 340,
    groundY: 400,
    frontArchRadius: 70,
    rearArchRadius: 72,
    scalePxPerInch: 4.434,
  },
};

export const PAINT_PRESETS: PaintPreset[] = [
  { id: 'hugger_orange', name: 'Hugger Orange', colorHex: '#ea580c', finishType: 'acrylic_lacquer', clearcoatGloss: 0.95, metallicFleckDensity: 0.0, eraDescription: '1969 Camaro SS / Daytona' },
  { id: 'marina_blue', name: 'Marina Blue', colorHex: '#1d4ed8', finishType: 'metallic_enamel', clearcoatGloss: 0.90, metallicFleckDensity: 0.7, eraDescription: '1967 Corvette Stingray' },
  { id: 'highland_green', name: 'Highland Green', colorHex: '#14532d', finishType: 'metallic_enamel', clearcoatGloss: 0.92, metallicFleckDensity: 0.65, eraDescription: '1968 Fastback Bullitt' },
  { id: 'plum_crazy', name: 'Plum Crazy Purple', colorHex: '#6b21a8', finishType: 'metallic_enamel', clearcoatGloss: 0.90, metallicFleckDensity: 0.75, eraDescription: '1970 Mopar High Impact' },
  { id: 'candy_apple_red', name: 'Candy Apple Red', colorHex: '#b91c1c', finishType: 'acrylic_lacquer', clearcoatGloss: 0.98, metallicFleckDensity: 0.3, eraDescription: 'Classic Deep Custom' },
  { id: 'wimbledon_white', name: 'Wimbledon White', colorHex: '#f8fafc', finishType: 'acrylic_lacquer', clearcoatGloss: 0.90, metallicFleckDensity: 0.0, eraDescription: '1965 GT350 Guardsman' },
  { id: 'tuxedo_black', name: 'Tuxedo Black', colorHex: '#09090b', finishType: 'acrylic_lacquer', clearcoatGloss: 1.0, metallicFleckDensity: 0.0, eraDescription: 'High Gloss Mirror Black' },
  { id: 'daytona_yellow', name: 'Daytona Yellow', colorHex: '#eab308', finishType: 'acrylic_lacquer', clearcoatGloss: 0.92, metallicFleckDensity: 0.0, eraDescription: '1969 Yenko High Impact' },
  { id: 'cortez_silver', name: 'Cortez Silver', colorHex: '#94a3b8', finishType: 'metallic_enamel', clearcoatGloss: 0.94, metallicFleckDensity: 0.85, eraDescription: '1970 Chevelle SS' },
  { id: 'grabber_blue', name: 'Grabber Blue', colorHex: '#0284c7', finishType: 'acrylic_lacquer', clearcoatGloss: 0.92, metallicFleckDensity: 0.0, eraDescription: '1970 Boss 302' },
  { id: 'burnt_bronze', name: 'Burnt Bronze', colorHex: '#78350f', finishType: 'metallic_enamel', clearcoatGloss: 0.88, metallicFleckDensity: 0.80, eraDescription: 'Modern Pro-Touring Metallic' },
  { id: 'gulf_light_blue', name: 'Gulf Heritage Blue', colorHex: '#38bdf8', finishType: 'acrylic_lacquer', clearcoatGloss: 0.93, metallicFleckDensity: 0.0, eraDescription: '1966 GT40 Le Mans' },
  { id: 'gulf_racing_orange', name: 'Gulf Racing Orange', colorHex: '#f97316', finishType: 'acrylic_lacquer', clearcoatGloss: 0.93, metallicFleckDensity: 0.0, eraDescription: '1966 GT40 Racing Stripe' },
  { id: 'hot_rod_flat_black', name: 'Hot Rod Flat Black', colorHex: '#18181b', finishType: 'satin_primer', clearcoatGloss: 0.15, metallicFleckDensity: 0.0, eraDescription: 'Satin Suede Hot Rod' },
  { id: 'red_oxide_primer', name: 'Red Oxide Primer', colorHex: '#7f1d1d', finishType: 'satin_primer', clearcoatGloss: 0.10, metallicFleckDensity: 0.0, eraDescription: 'Factory Floor Stamping Primer' },
  { id: 'gray_epoxy_primer', name: 'Gray Epoxy Primer', colorHex: '#475569', finishType: 'satin_primer', clearcoatGloss: 0.18, metallicFleckDensity: 0.0, eraDescription: 'High-Build Blocking Guide Coat' },
  { id: 'desert_tan_primer', name: 'Desert Tan Primer', colorHex: '#78716c', finishType: 'satin_primer', clearcoatGloss: 0.15, metallicFleckDensity: 0.0, eraDescription: 'Satin Rat Rod Khaki' },
  { id: 'olive_drab_matte', name: 'Olive Drab Matte', colorHex: '#365314', finishType: 'satin_primer', clearcoatGloss: 0.12, metallicFleckDensity: 0.0, eraDescription: 'Military Combat Tactical Satin' },
];

export const WORKSHOP_PRESETS: Record<string, VisualizerState> = {
  detroit_street_fighter: {
    version: 1,
    id: 'preset_detroit_street_fighter',
    name: '1969 Detroit Street Fighter',
    updatedAt: '2026-10-01T00:00:00.000Z',
    silhouetteId: 'classic_muscle',
    stance: {
      frontHeightOffsetInches: -2.0,
      rearHeightOffsetInches: -0.5,
      frontWheelDiameter: 15,
      rearWheelDiameter: 15,
      staggeredWheels: false,
    },
    paint: {
      finishType: 'acrylic_lacquer',
      colorHex: '#ea580c',
      presetName: 'Hugger Orange',
      clearcoatGloss: 0.95,
      metallicFleckDensity: 0.0,
    },
    wheel: {
      archetype: 'torq_thrusts',
      finish: 'machined_gray',
      tireTreatment: 'white_letter',
    },
    showDimensionHud: false,
  },
  pro_touring_track_monster: {
    version: 1,
    id: 'preset_pro_touring_track_monster',
    name: 'Pro-Touring Track Monster',
    updatedAt: '2026-10-01T00:00:00.000Z',
    silhouetteId: 'classic_muscle',
    stance: {
      frontHeightOffsetInches: -3.5,
      rearHeightOffsetInches: -3.0,
      frontWheelDiameter: 18,
      rearWheelDiameter: 18,
      staggeredWheels: false,
    },
    paint: {
      finishType: 'metallic_enamel',
      colorHex: '#14532d',
      presetName: 'Highland Green',
      clearcoatGloss: 0.90,
      metallicFleckDensity: 0.65,
    },
    wheel: {
      archetype: 'mesh',
      finish: 'gold',
      tireTreatment: 'blackwall',
      hasRivetBolts: true,
    },
    showDimensionHud: true,
  },
  slammed_shop_c10: {
    version: 1,
    id: 'preset_slammed_shop_c10',
    name: 'Slammed Shop C10 Truck',
    updatedAt: '2026-10-01T00:00:00.000Z',
    silhouetteId: 'vintage_truck',
    stance: {
      frontHeightOffsetInches: -4.0,
      rearHeightOffsetInches: -4.0,
      frontWheelDiameter: 20,
      rearWheelDiameter: 20,
      staggeredWheels: false,
    },
    paint: {
      finishType: 'satin_primer',
      colorHex: '#475569',
      presetName: 'Gray Epoxy Primer',
      clearcoatGloss: 0.18,
      metallicFleckDensity: 0.0,
    },
    wheel: {
      archetype: 'steelies',
      finish: 'chrome',
      tireTreatment: 'blackwall',
      hasBeautyRings: true,
    },
    showDimensionHud: false,
  },
  deuce_highboy_survivor: {
    version: 1,
    id: 'preset_deuce_highboy_survivor',
    name: '1932 Deuce Highboy Survivor',
    updatedAt: '2026-10-01T00:00:00.000Z',
    silhouetteId: 'deuce_roadster',
    stance: {
      frontHeightOffsetInches: 0.0,
      rearHeightOffsetInches: 1.0,
      frontWheelDiameter: 15,
      rearWheelDiameter: 17,
      staggeredWheels: true,
    },
    paint: {
      finishType: 'satin_primer',
      colorHex: '#18181b',
      presetName: 'Hot Rod Flat Black',
      clearcoatGloss: 0.15,
      metallicFleckDensity: 0.0,
    },
    wheel: {
      archetype: 'slotted_mags',
      finish: 'polished',
      tireTreatment: 'redline',
    },
    showDimensionHud: false,
  }
};
